import { AnimationContainer, Canvas, StickyZone } from './AnimatedLogo.style'
import {
    BackSide,
    BufferGeometry,
    CanvasTexture,
    FrontSide,
    Group,
    LinearFilter,
    Side,
    Texture,
} from 'three'
import React, { MutableRefObject, memo, useEffect, useMemo, useRef, useState } from 'react'
import { animated, useSpring } from 'react-spring/three'
import { useFrame, useResource } from 'react-three-fiber'

import FontFaceObserver from 'fontfaceobserver'
import { useThree } from 'react-three-fiber'

const degToRad = (degrees: number) => (Math.PI * degrees) / 180

type MaterialProps = {
    texture: Texture
    side: Side
}

const Material = ({ texture, side }: MaterialProps) => (
    <meshBasicMaterial attach="material" map={texture} color={'white'} side={side} transparent />
)

type MeshGroupProps = {
    setIsHover(isHover: boolean): void
    scale: number
    geometryRef: MutableRefObject<BufferGeometry>
    textureRef: MutableRefObject<CanvasTexture>
}

const MeshGroup = memo(({ setIsHover, scale, geometryRef, textureRef }: MeshGroupProps) => (
    <group
        scale={[scale, scale, scale]}
        onPointerOver={() => setIsHover(true)}
        onPointerOut={() => setIsHover(false)}
    >
        <mesh renderOrder={2} geometry={geometryRef.current}>
            <Material texture={textureRef.current} side={FrontSide} />
        </mesh>
        <mesh geometry={geometryRef.current}>
            <Material texture={textureRef.current} side={BackSide} />
        </mesh>
    </group>
))

type SegmentProps = {
    text: string
    setIsHover(isHover: boolean): void
}

const Segment = ({ text, setIsHover }: SegmentProps) => {
    const { gl } = useThree()
    const [geometryRef] = useResource<BufferGeometry>()
    const [textureRef] = useResource<CanvasTexture>()
    const fractionOfCircle = 1 // text.length / 32

    const canvas = useMemo(() => {
        const ctx = document.createElement('canvas').getContext('2d')!
        const canvasWidth = 4 * 1024
        const textHeight = 128
        ctx.canvas.width = canvasWidth
        ctx.font = `${textHeight}pt EverettMedium`
        const textWidth = ctx.measureText(text).width
        const aspectRatio = textWidth / (fractionOfCircle * textHeight)
        const canvasHeight = Math.ceil(canvasWidth / aspectRatio)
        ctx.canvas.height = 256
        ctx.font = `${textHeight}pt EverettMedium`
        ctx.fillStyle = 'black'

        ctx.scale(canvasWidth / textWidth, canvasHeight / textHeight)
        ctx.fillText(text, 0, textHeight - 1)

        return ctx.canvas
    }, [text, fractionOfCircle])

    const radiusTop = canvas.width / (2 * Math.PI),
        radiusFactor = 1.01,
        radiusBottom = radiusFactor * radiusTop,
        height = canvas.height,
        radialSegments = 64,
        heightSegments = 1,
        isOpenEnded = true,
        thetaStart = 0,
        thetaLength = fractionOfCircle * 2 * Math.PI,
        scale = 512 / canvas.width

    return (
        <>
            <cylinderBufferGeometry
                ref={geometryRef}
                args={[
                    radiusTop,
                    radiusBottom,
                    height,
                    radialSegments,
                    heightSegments,
                    isOpenEnded,
                    thetaStart,
                    thetaLength,
                ]}
            />
            <canvasTexture
                ref={textureRef}
                image={canvas}
                minFilter={LinearFilter}
                magFilter={LinearFilter}
                anisotropy={gl.capabilities.getMaxAnisotropy()}
            />
            {geometryRef.current && textureRef.current && (
                <MeshGroup
                    setIsHover={setIsHover}
                    geometryRef={geometryRef}
                    textureRef={textureRef}
                    scale={scale}
                />
            )}
        </>
    )
}

type SegmentsProps = {
    isClockwise: boolean
    setIsHover(hover: boolean): void
}

const Segments = memo(
    ({ isClockwise, setIsHover }: SegmentsProps) => {
        const group = useRef<Group>()
        // let rotationAY = 0,
        //     rotationBY = 75
        // const { rotationA, rotationB, props } = useSpring({
        //     from: {
        //         rotationA: [0, degToRad(rotationAY), 0],
        //         rotationB: [0, degToRad(rotationBY), 0],
        //     },
        //     to: async (next: any) => {
        //         while (true) {
        //             await next({
        //                 rotationA: [0, degToRad(rotationAY), 0],
        //                 rotationB: [0, degToRad(rotationBY), 0],
        //             })
        //             await next({
        //                 rotationA: [0, degToRad(isClockwise ? (rotationAY -= 200) : rotationAY), 0],
        //                 rotationB: [
        //                     0,
        //                     degToRad(!isClockwise ? (rotationBY += 200) : rotationBY),
        //                     0,
        //                 ],
        //             })
        //             await next({
        //                 rotationA: [0, degToRad(isClockwise ? rotationAY : (rotationAY += 200)), 0],
        //                 rotationB: [
        //                     0,
        //                     degToRad(!isClockwise ? rotationBY : (rotationBY -= 200)),
        //                     0,
        //                 ],
        //             })
        //         }
        //     },
        //     config: { mass: 20, tension: 1500, friction: 300, precision: 0.0002 },
        // })
        useFrame(() => {
            if (group.current) {
                group.current.rotation.y += isClockwise ? -0.005 : 0.005
            }
        })

        return (
            <>
                <animated.group ref={group} rotation={[0, 0, 0]}>
                    <Segment
                        text="MOVING BORDERS     MOVING BORDERS     "
                        setIsHover={setIsHover}
                    />
                </animated.group>
            </>
        )
    },
    (prev, curr) => prev.isClockwise === curr.isClockwise
)

type ViewProps = {
    isMobile: boolean
    scrollPosition: number
    isClockwise: boolean
}

export default ({ isMobile, scrollPosition, isClockwise }: ViewProps) => {
    const [isFontLoaded, setIsFontLoaded] = useState(false)
    const [isHover, setIsHover] = useState(false)

    useEffect(() => {
        new FontFaceObserver('EverettMedium').load(null, 5000).then(function () {
            setIsFontLoaded(true)
        })
    }, [])

    const { rotation, props } = useSpring({
        from: {
            rotation: [degToRad(23), 0, 0],
        },
        to: {
            rotation: [degToRad(23) + scrollPosition / 3000, scrollPosition / 600, 0],
        },
        config: { mass: 15, tension: 1500, friction: 300, precision: 0.0002 },
    })

    return (
        <StickyZone>
            <AnimationContainer>
                {isFontLoaded && (
                    <Canvas
                        camera={{
                            position: [0, isMobile ? -8 : 0, 475],
                            fov: 12,
                        }}
                        isHover={isHover}
                        pixelRatio={window.devicePixelRatio}
                    >
                        <animated.group rotation={rotation} {...props}>
                            <Segments isClockwise={isClockwise} setIsHover={setIsHover} />
                        </animated.group>
                    </Canvas>
                )}
            </AnimationContainer>
        </StickyZone>
    )
}
